import React, { useState, useEffect, useCallback } from "react";
import API_BASE_URL from "../config";
import Swal from "sweetalert2";
import {
    Eye, RefreshCw, X, Search, Plus,
    ClipboardList, Users, Building2, CalendarDays, Trash2, Save, FileText,
    Download
} from "lucide-react";

// ── Field accessors ──────────────────────────────────────────────────────────
const f = {
    id: r => r.id || r.Id || 0,
    controlNo: r => r.control_No || r.Control_No || r.controlNo || "",
    date: r => r.date || r.Date || "",
    compactor: r => r.compactor || r.Compactor || "",
    items: r => r.items || r.Items || [],
    documentNo: r => r.documentNo || r.DocumentNo || "",
    recordedBy: r => r.recordedBy || r.RecordedBy || "",
    created: r => r.created || r.Created || "",
};

const PAGE_SIZE = 10;
const DRAFT_KEY = "motorpool_draft";

// ── Scrap Codes ──────────────────────────────────────────────────────────────
// const SCRAP_CODES = [
//     { code: "PF",  name: "SCRP004-Pouch Film White" },
//     { code: "NP",  name: "SCRP008-Polybag No Print" },
//     { code: "PP",  name: "SCRP009-Polybag Printed" },
//     { code: "LP",  name: "SCRP011-Laminated Plastic" },
//     { code: "BWT", name: "SCRP006-Backsheet With Tape" },
//     { code: "BOT", name: "SCRP007-Backsheet Without Tape" },
//     { code: "BS",  name: "SCRP007-Backsheet Without Tape" },
//     { code: "WS",  name: "SCRP041-Jumbo Brown Sack" },
//     { code: "BH",  name: "SCRP042-Jumbo White Sack" },
//     { code: "AC",  name: "SCRP010-Black Hidden Plastic" },
//     { code: "TS",  name: "SCRP014-Assorted Carton" },
// ];

// NEW — scrap codes from API
// NEW — ilipat sa loob ng MotorpoolScrap component,
// after:  const [pdfLoading, setPdfLoading] = useState(false);

const EMPTY_ITEM = { time: "", scrapCode: "", scrapName: "", tagNo: "", quantity: "" };
const EMPTY_FORM = () => ({
    controlNo: "",
    date: new Date().toISOString().split("T")[0],
    compactor: "",
    documentNo: "",
    recordedBy: "",
    items: Array.from({ length: 5 }, () => ({ ...EMPTY_ITEM })),
});

// ── PDF Export via html2pdf.js ───────────────────────────────────────────────
const exportPDF = async (data, scrapCodes = []) => {
    if (!window.html2pdf) {
        await new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
            s.onload = resolve;
            s.onerror = reject;
            document.head.appendChild(s);
        });
    }

    const controlNo = data.control_No || data.controlNo || "export";
    const docDate = data.date
        ? new Date(data.date).toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" })
        : "";

    const rows = Array.from({ length: Math.max((data.items || []).length, 10) }, (_, i) => data.items?.[i] || {});

    const dataRows = rows.map((item, i) => `
        <tr style="background:${i % 2 === 0 ? "#fff" : "#f8fafc"}">
            <td style="text-align:center;color:#aaa;font-size:8.5px;padding:5px 3px;border-bottom:0.5px solid #edf0f4;border-right:0.5px solid #edf0f4">${i + 1}</td>
            <td style="text-align:center;font-size:9px;padding:5px 4px;border-bottom:0.5px solid #edf0f4;border-right:0.5px solid #edf0f4">${item.time || ""}</td>
            <td style="text-align:center;font-size:9px;font-weight:700;padding:5px 4px;border-bottom:0.5px solid #edf0f4;border-right:0.5px solid #edf0f4;color:#1e3a8a">${item.scrapCode || ""}</td>
            <td style="font-size:9px;padding:5px 8px;border-bottom:0.5px solid #edf0f4;border-right:0.5px solid #edf0f4">${item.scrapName || ""}</td>
            <td style="text-align:center;font-size:9px;padding:5px 4px;border-bottom:0.5px solid #edf0f4;border-right:0.5px solid #edf0f4">${item.tagNo || ""}</td>
            <td style="text-align:center;font-size:9px;font-weight:600;padding:5px 4px;border-bottom:0.5px solid #edf0f4">${(item.quantity != null && item.quantity !== "") ? item.quantity : ""}</td>
        </tr>`).join("");

    const scrapRows = scrapCodes.map((sc, i) => `
        <tr>
            <td style="padding:2px 3px;font-size:7px;color:#94a3b8;text-align:center;border-bottom:0.5px solid #f1f5f9;width:14px">${i + 1}</td>
            <td style="padding:2px 5px;font-size:7px;color:#334155;border-bottom:0.5px solid #f1f5f9">${sc.name}</td>
        </tr>`).join("");

    const codeRows = scrapCodes.map(sc => `
        <tr>
            <td style="padding:2px 5px;font-size:7.5px;font-weight:700;text-align:center;color:#1e3a8a;border-bottom:0.5px solid #f1f5f9">${sc.code}</td>
        </tr>`).join("");

    const html = `<div style="font-family:Arial,sans-serif;background:#fff;width:190mm;padding:8mm 8mm 6mm">

        <!-- HEADER BAND -->
        <div style="background:linear-gradient(135deg,#0f172a 0%,#1e3a8a 55%,#1d4ed8 100%);border-radius:8px;padding:12px 16px;display:flex;align-items:center;justify-content:space-between;margin-bottom:12px">
            <div style="display:flex;align-items:center;gap:10px">
                <div style="width:36px;height:36px;border-radius:50%;border:2px solid rgba(255,255,255,0.4);display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,0.1);flex-shrink:0">
                    <span style="color:#fff;font-size:9px;font-weight:900;letter-spacing:0.5px">MS</span>
                </div>
                <div>
                    <div style="color:#93c5fd;font-size:7px;font-weight:700;letter-spacing:0.15em;text-transform:uppercase;margin-bottom:2px">Mega Soft · Spareparts Department</div>
                    <div style="color:#fff;font-size:15px;font-weight:900">Motorpool Area Compacted Scrap</div>
                </div>
            </div>
            <div style="text-align:right;background:rgba(255,255,255,0.12);border:1px solid rgba(255,255,255,0.2);border-radius:6px;padding:6px 14px">
                <div style="color:#93c5fd;font-size:7px;font-weight:700;letter-spacing:0.15em;text-transform:uppercase">Control No.</div>
                <div style="color:#fff;font-size:15px;font-weight:900;letter-spacing:1px">${controlNo}</div>
            </div>
        </div>

        <!-- META ROW -->
        <div style="display:flex;align-items:center;gap:0;margin-bottom:10px;padding:7px 12px;background:#f8fafc;border-radius:6px;border:0.5px solid #e2e8f0">
            <div style="display:flex;align-items:center;gap:5px;padding-right:14px">
                <span style="font-size:7.5px;color:#64748b;font-weight:700;text-transform:uppercase;letter-spacing:0.08em">Date:</span>
                <span style="font-size:9px;font-weight:700;color:#0f172a">${docDate}</span>
            </div>
            <div style="width:1px;height:14px;background:#cbd5e1;margin-right:14px"></div>
            <div style="display:flex;align-items:center;gap:5px;padding-right:14px">
                <span style="font-size:7.5px;color:#64748b;font-weight:700;text-transform:uppercase;letter-spacing:0.08em">Compactor:</span>
                <span style="font-size:9px;font-weight:700;color:#0f172a">${data.compactor || ""}</span>
            </div>
            <div style="width:1px;height:14px;background:#cbd5e1;margin-right:14px"></div>
            <div style="display:flex;align-items:center;gap:5px;padding-right:14px">
                <span style="font-size:7.5px;color:#64748b;font-weight:700;text-transform:uppercase;letter-spacing:0.08em">Document No.:</span>
                <span style="font-size:9px;font-weight:700;color:#1d4ed8">${data.documentNo || "—"}</span>
            </div>
            <div style="width:1px;height:14px;background:#cbd5e1;margin-right:14px"></div>
            <div style="display:flex;align-items:center;gap:5px">
                <span style="font-size:7.5px;color:#64748b;font-weight:700;text-transform:uppercase;letter-spacing:0.08em">Unit:</span>
                <span style="font-size:9px;font-weight:700;color:#0f172a">kgs.</span>
            </div>
        </div>

        <!-- ITEMS TABLE -->
        <table style="width:100%;border-collapse:collapse;margin-bottom:10px;border:0.5px solid #e2e8f0;border-radius:6px;overflow:hidden">
            <thead>
                <tr style="background:#1e3a8a">
                    <th style="padding:7px 4px;font-size:7.5px;font-weight:700;text-transform:uppercase;letter-spacing:0.07em;color:#fff;text-align:center;width:4%;border-right:0.5px solid rgba(255,255,255,0.15)">#</th>
                    <th style="padding:7px 4px;font-size:7.5px;font-weight:700;text-transform:uppercase;letter-spacing:0.07em;color:#fff;text-align:center;width:9%;border-right:0.5px solid rgba(255,255,255,0.15)">Time</th>
                    <th style="padding:7px 4px;font-size:7.5px;font-weight:700;text-transform:uppercase;letter-spacing:0.07em;color:#fff;text-align:center;width:10%;border-right:0.5px solid rgba(255,255,255,0.15)">Scrap Code</th>
                    <th style="padding:7px 8px;font-size:7.5px;font-weight:700;text-transform:uppercase;letter-spacing:0.07em;color:#fff;text-align:left;width:38%;border-right:0.5px solid rgba(255,255,255,0.15)">Scrap Name</th>
                    <th style="padding:7px 4px;font-size:7.5px;font-weight:700;text-transform:uppercase;letter-spacing:0.07em;color:#fff;text-align:center;width:22%;border-right:0.5px solid rgba(255,255,255,0.15)">Tag No.</th>
                    <th style="padding:7px 4px;font-size:7.5px;font-weight:700;text-transform:uppercase;letter-spacing:0.07em;color:#fff;text-align:center;width:17%">Quantity (kgs.)</th>
                </tr>
            </thead>
            <tbody>${dataRows}</tbody>
        </table>

        <!-- BOTTOM: Legend + Document Info -->
        <div style="display:flex;gap:10px">
            <!-- Legend table -->
            <div style="width:200px;flex-shrink:0;border:0.5px solid #e2e8f0;border-radius:6px;overflow:hidden">
                <div style="display:flex">
                    <div style="flex:1">
                        <div style="background:#f1f5f9;padding:5px 6px;font-size:7px;font-weight:800;color:#334155;text-transform:uppercase;letter-spacing:0.07em;border-bottom:0.5px solid #e2e8f0">Scrap Code – Name</div>
                        <table style="width:100%;border-collapse:collapse"><tbody>${scrapRows}</tbody></table>
                    </div>
                    <div style="width:38px;border-left:0.5px solid #e2e8f0">
                        <div style="background:#f1f5f9;padding:5px 4px;font-size:7px;font-weight:800;color:#334155;text-align:center;border-bottom:0.5px solid #e2e8f0;text-transform:uppercase">Code</div>
                        <table style="width:100%;border-collapse:collapse"><tbody>${codeRows}</tbody></table>
                    </div>
                </div>
            </div>

            <!-- Document No + Recorded By -->
            <div style="flex:1;border:0.5px solid #e2e8f0;border-radius:6px;padding:16px 20px;display:flex;flex-direction:column;justify-content:space-between">
                <div>
                    <div style="font-size:7.5px;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:0.12em;margin-bottom:6px">Document No.</div>
                    <div style="font-size:22px;font-weight:900;color:#1d4ed8;letter-spacing:1px;padding-bottom:4px;border-bottom:1.5px solid #dbeafe;display:inline-block;min-width:120px">${data.documentNo || ""}</div>
                </div>
                <div style="margin-top:20px">
                    <div style="font-size:7.5px;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:0.12em;margin-bottom:22px">Recorded By:</div>
                    <div style="font-size:11px;font-weight:700;color:#0f172a;border-bottom:1.5px solid #0f172a;padding-bottom:3px;display:inline-block;min-width:120px">${data.recordedBy || ""}</div>
                    <div style="font-size:7px;color:#94a3b8;margin-top:4px;font-style:italic">Signature over Printed Name</div>
                </div>
            </div>
        </div>

        <!-- FOOTER -->
        <div style="margin-top:10px;padding-top:7px;border-top:0.5px solid #e2e8f0;text-align:center">
            <p style="font-size:7px;color:#94a3b8;font-style:italic;margin:0">
                This document is a property of Mega Soft Hygienic Products, Inc. Unauthorized copying, reproduction, distribution, or alteration is strictly prohibited.
            </p>
        </div>
    </div>`;

    const el = document.createElement("div");
    el.innerHTML = html;
    document.body.appendChild(el);

    await window.html2pdf(el, {
        margin: [4, 4, 4, 4],
        filename: `MotorpoolScrap_${controlNo}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    });

    document.body.removeChild(el);
};

// ── Document Preview (View Modal) ────────────────────────────────────────────
const MotorpoolDocument = ({ data, scrapCodes = [] }) => {
    const rows = Array.from({ length: Math.max((data.items || []).length, 10) }, (_, i) => data.items?.[i] || {});
    const docDate = data.date
        ? new Date(data.date).toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" })
        : "";

    const TH = ({ children, center, w, style = {} }) => (
        <th style={{
            padding: "8px 6px",
            fontSize: "9px",
            fontWeight: "700",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: "#fff",
            backgroundColor: "#1e3a8a",
            borderRight: "0.5px solid rgba(255,255,255,0.15)",
            textAlign: center ? "center" : "left",
            width: w || "auto",
            whiteSpace: "nowrap",
            ...style,
        }}>{children}</th>
    );

    return (
        <div style={{ fontFamily: "Arial, sans-serif" }}>

            {/* Header band */}
            <div style={{ background: "linear-gradient(135deg,#0f172a 0%,#1e3a8a 55%,#1d4ed8 100%)", padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div style={{ width: "40px", height: "40px", borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.1)", flexShrink: 0 }}>
                        <span style={{ color: "#fff", fontSize: "9px", fontWeight: "900" }}>MS</span>
                    </div>
                    <div>
                        <div style={{ color: "#93c5fd", fontSize: "8px", fontWeight: "700", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "2px" }}>Mega Soft · Spareparts Department</div>
                        <div style={{ color: "#fff", fontSize: "16px", fontWeight: "900" }}>Motorpool Area Compacted Scrap</div>
                    </div>
                </div>
                <div style={{ textAlign: "right", background: "rgba(255,255,255,0.1)", border: "0.5px solid rgba(255,255,255,0.2)", borderRadius: "8px", padding: "7px 14px" }}>
                    <div style={{ color: "#93c5fd", fontSize: "7.5px", fontWeight: "700", letterSpacing: "0.15em", textTransform: "uppercase" }}>Control No.</div>
                    <div style={{ color: "#fff", fontSize: "16px", fontWeight: "900", letterSpacing: "1.5px" }}>{data.control_No || data.controlNo || "—"}</div>
                </div>
            </div>

            {/* Meta bar */}
            <div style={{ display: "flex", alignItems: "center", gap: "0", padding: "9px 16px", background: "#f8fafc", borderBottom: "0.5px solid #e2e8f0", flexWrap: "wrap" }}>
                {[
                    { label: "Date", value: docDate },
                    { label: "Compactor", value: data.compactor || data.Compactor || "—" },
                    { label: "Document No.", value: data.documentNo || data.DocumentNo || "—", blue: true },
                    { label: "Unit", value: "kgs." },
                ].map((m, i) => (
                    <React.Fragment key={i}>
                        {i > 0 && <span style={{ width: "1px", height: "14px", background: "#cbd5e1", display: "inline-block", margin: "0 14px" }} />}
                        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <span style={{ fontSize: "8px", color: "#64748b", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.08em" }}>{m.label}:</span>
                            <span style={{ fontSize: "11px", fontWeight: "700", color: m.blue ? "#1d4ed8" : "#0f172a" }}>{m.value}</span>
                        </div>
                    </React.Fragment>
                ))}
            </div>

            {/* Items table */}
            <div style={{ padding: "14px 16px 10px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", border: "0.5px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
                    <thead>
                        <tr>
                            <TH center w="4%">#</TH>
                            <TH center w="9%">Time</TH>
                            <TH center w="10%">Scrap Code</TH>
                            <TH w="37%" style={{ paddingLeft: "10px" }}>Scrap Name</TH>
                            <TH center w="22%">Tag No.</TH>
                            <TH center w="18%" style={{ borderRight: "none" }}>Quantity (kgs.)</TH>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((item, i) => {
                            const bg = i % 2 === 0 ? "#fff" : "#f8fafc";
                            const td = { padding: "7px 5px", fontSize: "10.5px", borderBottom: "0.5px solid #edf0f4", borderRight: "0.5px solid #edf0f4", verticalAlign: "middle", backgroundColor: bg };
                            const has = !!(item.scrapCode || item.scrapName || item.tagNo || item.quantity);
                            return (
                                <tr key={i}>
                                    <td style={{ ...td, textAlign: "center", color: "#9ca3af", fontSize: "9px" }}>{i + 1}</td>
                                    <td style={{ ...td, textAlign: "center" }}>{item.time || item.Time || ""}</td>
                                    <td style={{ ...td, textAlign: "center", fontWeight: has ? "700" : "400", color: has ? "#1e3a8a" : "inherit" }}>{item.scrapCode || item.ScrapCode || ""}</td>
                                    <td style={{ ...td, paddingLeft: "10px" }}>{item.scrapName || item.ScrapName || ""}</td>
                                    <td style={{ ...td, textAlign: "center" }}>{item.tagNo || item.TagNo || ""}</td>
                                    <td style={{ ...td, textAlign: "center", fontWeight: has ? "700" : "400", borderRight: "none" }}>{item.quantity ?? item.Quantity ?? ""}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Bottom: Legend + Doc Info */}
            <div style={{ display: "flex", gap: "12px", padding: "0 16px 14px" }}>
                {/* Legend */}
                <div style={{ width: "215px", flexShrink: 0, border: "0.5px solid #e2e8f0", borderRadius: "6px", overflow: "hidden" }}>
                    <div style={{ display: "flex" }}>
                        <div style={{ flex: 1 }}>
                            <div style={{ background: "#f1f5f9", padding: "5px 6px", fontSize: "7.5px", fontWeight: "800", color: "#334155", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "0.5px solid #e2e8f0" }}>
                                Scrap Code – Name
                            </div>
                            {scrapCodes.map((sc, i) => (
                                <div key={sc.fullCode} style={{ display: "flex", padding: "3px 0", borderBottom: "0.5px solid #f1f5f9" }}>
                                    <span style={{ width: "18px", textAlign: "center", fontSize: "7.5px", color: "#94a3b8", flexShrink: 0 }}>{i + 1}</span>
                                    <span style={{ fontSize: "7.5px", color: "#334155", paddingLeft: "4px" }}>{sc.name}</span>
                                </div>
                            ))}
                        </div>
                        <div style={{ width: "40px", borderLeft: "0.5px solid #e2e8f0" }}>
                            <div style={{ background: "#f1f5f9", padding: "5px 4px", fontSize: "7.5px", fontWeight: "800", color: "#334155", textAlign: "center", borderBottom: "0.5px solid #e2e8f0", textTransform: "uppercase" }}>Code</div>
                            {scrapCodes.map((sc, i) => (
                                <div key={sc.fullCode} style={{ padding: "3px 4px", textAlign: "center", fontSize: "9px", fontWeight: "800", color: "#1e3a8a", borderBottom: "0.5px solid #f1f5f9" }}>
                                    {sc.code}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Document info */}
                <div style={{ flex: 1, border: "0.5px solid #e2e8f0", borderRadius: "6px", padding: "18px 22px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                    <div>
                        <div style={{ fontSize: "8px", fontWeight: "800", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "8px" }}>Document No.</div>
                        <div style={{ fontSize: "26px", fontWeight: "900", color: "#1d4ed8", letterSpacing: "1px", paddingBottom: "6px", borderBottom: "2px solid #dbeafe", display: "inline-block", minWidth: "140px" }}>
                            {data.documentNo || data.DocumentNo || "\u00A0"}
                        </div>
                    </div>
                    <div style={{ marginTop: "20px" }}>
                        <div style={{ fontSize: "8px", fontWeight: "800", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "24px" }}>Recorded By:</div>
                        <div style={{ fontSize: "13px", fontWeight: "700", color: "#0f172a", borderBottom: "1.5px solid #0f172a", paddingBottom: "4px", display: "inline-block", minWidth: "140px" }}>
                            {data.recordedBy || data.RecordedBy || "\u00A0".repeat(20)}
                        </div>
                        <div style={{ fontSize: "7.5px", color: "#94a3b8", marginTop: "5px", fontStyle: "italic" }}>Signature over Printed Name</div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div style={{ padding: "8px 16px", borderTop: "0.5px solid #e2e8f0", background: "#f8fafc", textAlign: "center" }}>
                <p style={{ fontSize: "7.5px", color: "#94a3b8", fontStyle: "italic", margin: 0 }}>
                    This document is a property of Mega Soft Hygienic Products, Inc. Unauthorized copying, reproduction, distribution, or alteration is strictly prohibited.
                </p>
            </div>
        </div>
    );
};

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────
const MotorpoolScrap = () => {
    const [currentUser, setCurrentUser] = useState(null);
    useEffect(() => {
        const stored = localStorage.getItem("user");
        if (stored) { try { setCurrentUser(JSON.parse(stored)); } catch { } }
    }, []);
    const userName = currentUser?.fullName || currentUser?.name || currentUser?.username || "Unknown User";

    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRow, setSelectedRow] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [scrapCodes, setScrapCodes] = useState([]);
    useEffect(() => {
        fetch(`${API_BASE_URL}api/ScrapCodes`)
            .then(r => r.ok ? r.json() : [])
            .then(data => {
                const mapped = (data || [])
                    .filter(s => s.isActive)
                    .map(s => ({
                        code: s.item_No.slice(-2),
                        fullCode: s.item_No,
                        name: s.item_Description,
                    }));
                setScrapCodes(mapped);
            })
            .catch(() => { });
    }, []);
    const [hasDraft, setHasDraft] = useState(() => !!localStorage.getItem(DRAFT_KEY));
    const [form, setForm] = useState(EMPTY_FORM());

    // ── GET ──────────────────────────────────────────────────────────────────
    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/MotorpoolScrap`);
            if (!res.ok) throw new Error(`Server error: ${res.status}`);
            setRecords(await res.json());
        } catch (err) {
            Swal.fire({ icon: "error", title: "Failed to load records", text: err.message, confirmButtonColor: "#dc2626" });
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { fetchRecords(); }, [fetchRecords]);

    // ── Control No generator ──
    const generateControlNo = useCallback(() => {
        const now = new Date();
        const yy = String(now.getFullYear()).slice(-2);
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const dd = String(now.getDate()).padStart(2, "0");
        const prefix = `${yy}-${mm}${dd}-`;
        const maxSeq = records
            .map(r => f.controlNo(r))
            .filter(cn => cn.startsWith(prefix))
            .map(cn => parseInt(cn.replace(prefix, ""), 10))
            .filter(n => !isNaN(n))
            .reduce((max, n) => Math.max(max, n), 0);
        return `${prefix}${String(maxSeq + 1).padStart(5, "0")}`;
    }, [records]);

    // ── Draft helpers ──
    const saveDraft = () => {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
        setHasDraft(true);
        Swal.fire({ icon: "success", title: "Draft Saved!", timer: 1500, showConfirmButton: false });
    };
    const clearDraft = () => { localStorage.removeItem(DRAFT_KEY); setHasDraft(false); };

    const openForm = () => {
        setForm({ ...EMPTY_FORM(), controlNo: generateControlNo(), recordedBy: userName });
        setShowForm(true);
    };
    const openDraft = () => {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (!raw) return;
        try { setForm(JSON.parse(raw)); } catch { }
        setShowForm(true);
    };

    // ── Item helpers ──
    const updateItem = (idx, field, value) => {
        setForm(prev => {
            const items = [...prev.items];
            items[idx] = { ...items[idx], [field]: value };
            // NEW
            if (field === "scrapCode") {
                const found = scrapCodes.find(s => s.code === value);
                items[idx].scrapName = found ? found.name : "";
            }
            return { ...prev, items };
        });
    };
    const addRow = () => setForm(prev => ({ ...prev, items: [...prev.items, { ...EMPTY_ITEM }] }));
    const removeRow = idx => setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));

    // ── POST ─────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!form.controlNo)
            return Swal.fire({ icon: "warning", title: "Control No. missing", confirmButtonColor: "#1d4ed8" });
        const filledItems = form.items.filter(i => i.scrapCode || i.scrapName || i.tagNo || i.quantity);
        if (!filledItems.length)
            return Swal.fire({ icon: "warning", title: "No items", text: "Please add at least one scrap item.", confirmButtonColor: "#1d4ed8" });

        setFormLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/MotorpoolScrap`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ control_No: form.controlNo, date: form.date, compactor: form.compactor, documentNo: form.documentNo, recordedBy: form.recordedBy, createdBy: userName, items: filledItems }),
            });
            if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e?.message || `Server error: ${res.status}`); }
            await Swal.fire({ icon: "success", title: "Saved!", text: "Motorpool Scrap record created.", timer: 2000, showConfirmButton: false });
            fetch(`${API_BASE_URL}api/Logs`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "CREATE", module: "Motorpool Scrap", description: `Created Motorpool Scrap #${form.controlNo}`, userId: currentUser?.id, username: currentUser?.username, fullName: userName }) }).catch(() => { });
            clearDraft(); setShowForm(false); fetchRecords();
        } catch (err) {
            Swal.fire({ icon: "error", title: "Save Failed", text: err.message, confirmButtonColor: "#dc2626" });
        } finally { setFormLoading(false); }
    };

    // ── DELETE ───────────────────────────────────────────────────────────────
    const handleDelete = async (row) => {
        const id = f.id(row), ctrlNo = f.controlNo(row);
        const { isConfirmed } = await Swal.fire({
            icon: "warning", title: "Delete Record?",
            html: `Are you sure you want to delete <br/><strong>${ctrlNo}</strong>?<br/>This action cannot be undone.`,
            showCancelButton: true, confirmButtonText: "Yes, Delete", cancelButtonText: "Cancel",
            confirmButtonColor: "#dc2626", cancelButtonColor: "#6b7280",
        });
        if (!isConfirmed) return;
        try {
            const res = await fetch(`${API_BASE_URL}api/MotorpoolScrap/${id}`, { method: "DELETE" });
            if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e?.message || `Server error: ${res.status}`); }
            await Swal.fire({ icon: "success", title: "Deleted!", text: `Record ${ctrlNo} deleted.`, timer: 1800, showConfirmButton: false });
            fetch(`${API_BASE_URL}api/Logs`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "DELETE", module: "Motorpool Scrap", description: `Deleted Motorpool Scrap #${ctrlNo}`, userId: currentUser?.id, username: currentUser?.username, fullName: userName }) }).catch(() => { });
            fetchRecords();
        } catch (err) {
            Swal.fire({ icon: "error", title: "Delete Failed", text: err.message, confirmButtonColor: "#dc2626" });
        }
    };

    // ── PDF Export ───────────────────────────────────────────────────────────
    const handleExportPDF = async (row) => {
        setPdfLoading(true);
        try { await exportPDF(row, scrapCodes); }
        catch (err) { Swal.fire({ icon: "error", title: "PDF Export Failed", text: err.message, confirmButtonColor: "#dc2626" }); }
        finally { setPdfLoading(false); }
    };

    // ── Filter + paginate ──
    const filtered = records.filter(row => {
        const q = search.toLowerCase();
        return !q || f.controlNo(row).toLowerCase().includes(q) || f.compactor(row).toLowerCase().includes(q) || f.documentNo(row).toLowerCase().includes(q) || f.recordedBy(row).toLowerCase().includes(q);
    });
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const paginBtn = (d) => `px-3 py-1.5 rounded-md text-xs font-semibold border transition-all ${d ? "border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed" : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50 cursor-pointer"}`;
    const gradHdr = { background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1d4ed8 100%)" };

    // ────────────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-slate-100 p-8 font-sans">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden w-full">

                {/* PAGE HEADER */}
                <div style={gradHdr} className="px-8 py-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="bg-white/15 border border-white/20 p-2.5 rounded-2xl"><ClipboardList className="text-white" size={22} /></div>
                        <div>
                            <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em] mb-0.5">Mega Soft · Spareparts Department</p>
                            <h1 className="text-white font-extrabold text-xl leading-tight">Motorpool Area Compacted Scrap</h1>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 bg-white/15 border border-white/20 rounded-lg px-3 py-1.5">
                            <Users size={14} className="text-white/70" />
                            <span className="text-white text-sm font-semibold">{userName}</span>
                        </div>
                        {hasDraft && (
                            <button onClick={openDraft} className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-bold border-0 cursor-pointer transition-all hover:-translate-y-0.5"
                                style={{ background: "linear-gradient(135deg,#92400e,#b45309)", boxShadow: "0 2px 10px rgba(180,83,9,0.40)" }}>
                                <FileText size={15} /> Restore Draft
                            </button>
                        )}
                        <button onClick={openForm} className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-bold border-0 cursor-pointer transition-all hover:-translate-y-0.5"
                            style={{ background: "linear-gradient(135deg,#15803d,#16a34a)", boxShadow: "0 2px 10px rgba(22,163,74,0.35)" }}>
                            <Plus size={16} /> New Record
                        </button>
                    </div>
                </div>

                <div className="p-8">
                    {/* TOOLBAR */}
                    <div className="flex flex-wrap gap-3 mb-4 items-center">
                        <div className="relative flex-1 min-w-[220px]">
                            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input type="text" placeholder="Search control no, compactor, document no..."
                                value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                        </div>
                        <button onClick={fetchRecords} className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer">
                            <RefreshCw size={15} />
                        </button>
                    </div>

                    {!loading && <p className="text-xs text-slate-500 mb-3">{filtered.length} record{filtered.length !== 1 ? "s" : ""} found</p>}

                    {/* TABLE */}
                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-slate-400 gap-3">
                            <RefreshCw size={20} className="animate-spin" />
                            <span className="text-sm font-medium">Loading records...</span>
                        </div>
                    ) : paginated.length === 0 ? (
                        <div className="text-center py-16 text-slate-400 text-sm">No records found.</div>
                    ) : (
                        <div className="overflow-x-auto rounded-xl border border-gray-200">
                            <table className="w-full text-sm border-collapse">
                                <thead>
                                    <tr className="bg-blue-800 text-white">
                                        {["#", "Control No", "Date", "Name of Compactor", "Document No", "Recorded By", "Items", "Actions"].map((h, i, arr) => (
                                            <th key={i} className={`px-3 py-3 text-left text-xs font-bold uppercase tracking-wide whitespace-nowrap ${h === "Actions" || h === "Items" ? "text-center" : ""} ${i === 0 ? "rounded-tl-xl" : ""} ${i === arr.length - 1 ? "rounded-tr-xl" : ""}`}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map((row, idx) => (
                                        <tr key={f.id(row) || idx} className={`border-b border-gray-100 transition-colors hover:bg-blue-50 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                                            <td className="px-3 py-3 text-center text-slate-400 text-xs w-10">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                                            <td className="px-3 py-3 font-bold text-blue-700 whitespace-nowrap">{f.controlNo(row) || "—"}</td>
                                            <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{f.date(row) ? new Date(f.date(row)).toLocaleDateString("en-US") : "—"}</td>
                                            <td className="px-3 py-3 text-slate-700">{f.compactor(row) || "—"}</td>
                                            <td className="px-3 py-3 text-slate-700">{f.documentNo(row) || "—"}</td>
                                            <td className="px-3 py-3 text-slate-700">{f.recordedBy(row) || "—"}</td>
                                            <td className="px-3 py-3 text-center">
                                                <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full">{(f.items(row) || []).length}</span>
                                            </td>
                                            <td className="px-3 py-3 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button onClick={() => setSelectedRow(row)}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-xs font-semibold hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all cursor-pointer">
                                                        <Eye size={13} /> View
                                                    </button>
                                                    <button onClick={() => handleDelete(row)}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-xs font-semibold hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all cursor-pointer">
                                                        <Trash2 size={13} /> Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* PAGINATION */}
                    {!loading && filtered.length > PAGE_SIZE && (
                        <div className="flex justify-between items-center mt-5">
                            <span className="text-xs text-slate-500">Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
                            <div className="flex gap-1.5 items-center">
                                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className={paginBtn(currentPage === 1)}>«</button>
                                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className={paginBtn(currentPage === 1)}>‹</button>
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let page;
                                    if (totalPages <= 5) page = i + 1;
                                    else if (currentPage <= 3) page = i + 1;
                                    else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
                                    else page = currentPage - 2 + i;
                                    return (
                                        <button key={page} onClick={() => setCurrentPage(page)}
                                            className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-all cursor-pointer ${page === currentPage ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}>
                                            {page}
                                        </button>
                                    );
                                })}
                                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className={paginBtn(currentPage === totalPages)}>›</button>
                                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className={paginBtn(currentPage === totalPages)}>»</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* PAGE FOOTER */}
                <div className="px-8 py-3 border-t border-gray-100 bg-slate-50 flex justify-between items-center">
                    <p className="text-[10px] text-slate-400 italic">This document is a property of Mega Soft. Unauthorized copying, reproduction, distribution, or alteration is strictly prohibited.</p>
                    <span className="text-xs text-slate-400">{new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                </div>
            </div>

            {/* ══════════════ NEW RECORD FORM MODAL ══════════════ */}
            {showForm && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
                    <div className="bg-white rounded-3xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden" style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.40)" }}>

                        <div style={gradHdr} className="px-7 py-5 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="bg-white/15 border border-white/20 p-2.5 rounded-2xl"><ClipboardList size={20} className="text-white" /></div>
                                <div>
                                    <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em]">New Entry</p>
                                    <h2 className="text-white font-extrabold text-base">Motorpool Area Compacted Scrap</h2>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-right">
                                    <p className="text-blue-300 text-[9px] font-bold uppercase tracking-widest">Control No.</p>
                                    <p className="text-white font-extrabold text-sm tracking-widest">{form.controlNo}</p>
                                </div>
                                <button onClick={() => setShowForm(false)} className="bg-white/15 hover:bg-white/25 text-white rounded-xl w-9 h-9 flex items-center justify-center cursor-pointer transition-all" style={{ border: "none" }}>
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-7 bg-slate-50">
                            {/* Doc Info */}
                            <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Document Information</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Control No.</label>
                                        <div className="w-full border-2 border-blue-100 bg-blue-50 rounded-xl px-3 py-2.5 text-sm font-extrabold text-blue-700 tracking-widest select-all">{form.controlNo}</div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Date</label>
                                        <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Name of Compactor</label>
                                        <input type="text" value={form.compactor} placeholder="Enter name..." onChange={e => setForm(p => ({ ...p, compactor: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Document No.</label>
                                        <input type="text" value={form.documentNo} placeholder="e.g. C2# 91443" onChange={e => setForm(p => ({ ...p, documentNo: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Recorded By</label>
                                        <input type="text" value={form.recordedBy} onChange={e => setForm(p => ({ ...p, recordedBy: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                </div>
                            </div>

                            {/* Items */}
                            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-5" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Scrap Items</h3>
                                    <button onClick={addRow} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-bold cursor-pointer hover:bg-blue-700 transition-colors" style={{ border: "none" }}>
                                        <Plus size={13} /> Add Row
                                    </button>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm border-collapse">
                                        <thead>
                                            <tr className="bg-slate-50 border-b border-gray-100">
                                                {["#", "Time", "Scrap Code", "Scrap Name", "Tag No.", "Qty (kgs.)", ""].map((h, i) => (
                                                    <th key={i} className="px-3 py-2.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {form.items.map((item, idx) => (
                                                <tr key={idx} className={`border-b border-gray-50 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}`}>
                                                    <td className="px-3 py-2 text-center text-slate-400 text-xs w-8">{idx + 1}</td>
                                                    <td className="px-2 py-2 w-28"><input type="time" value={item.time} onChange={e => updateItem(idx, "time", e.target.value)} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white" /></td>
                                                    <td className="px-2 py-2 w-28">
                                                        <select value={item.scrapCode} onChange={e => updateItem(idx, "scrapCode", e.target.value)} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white cursor-pointer font-bold">
                                                            <option value="">—</option>
                                                            {scrapCodes.map(sc => (
                                                                <option key={sc.fullCode} value={sc.code}>
                                                                    {sc.code} - {sc.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td className="px-2 py-2"><input type="text" value={item.scrapName} placeholder="Scrap name..." onChange={e => updateItem(idx, "scrapName", e.target.value)} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white" /></td>
                                                    <td className="px-2 py-2 w-36"><input type="text" value={item.tagNo} placeholder="Tag No." onChange={e => updateItem(idx, "tagNo", e.target.value)} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white" /></td>
                                                    <td className="px-2 py-2 w-28"><input type="number" value={item.quantity} placeholder="0" min="0" step="0.01" onChange={e => updateItem(idx, "quantity", e.target.value)} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white" /></td>
                                                    <td className="px-2 py-2 w-8">
                                                        {form.items.length > 1 && (
                                                            <button onClick={() => removeRow(idx)} className="text-red-400 hover:text-red-600 cursor-pointer p-1 rounded transition-colors" style={{ border: "none", background: "transparent" }}>
                                                                <Trash2 size={13} />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Scrap Code Reference */}
                            <div className="bg-white border border-gray-200 rounded-2xl p-4" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">Scrap Code Reference</p>
                                <div className="grid grid-cols-1 gap-0.5">

                                    {scrapCodes.map((sc, i) => (
                                        <div key={sc.fullCode} className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-slate-50">
                                            <span className="text-xs font-bold text-slate-400 w-5 text-right flex-shrink-0">{i + 1}.</span>
                                            <span className="text-xs font-bold text-blue-600 w-8 flex-shrink-0">{sc.code}</span>
                                            <span className="text-xs text-slate-600">{sc.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="px-7 py-4 bg-white border-t border-gray-100 flex items-center justify-between flex-shrink-0" style={{ boxShadow: "0 -4px 16px rgba(0,0,0,0.06)" }}>
                            <button onClick={saveDraft} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-amber-700 text-sm font-bold transition-all cursor-pointer hover:-translate-y-0.5" style={{ background: "#fef3c7", border: "2px solid #fde68a" }}>
                                <FileText size={14} /> Save Draft
                            </button>
                            <div className="flex items-center gap-3">
                                <button onClick={() => setShowForm(false)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 text-sm font-bold transition-all cursor-pointer">
                                    <X size={14} /> Cancel
                                </button>
                                <button onClick={handleSubmit} disabled={formLoading}
                                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-bold cursor-pointer disabled:opacity-50 transition-all hover:-translate-y-0.5"
                                    style={{ background: "linear-gradient(135deg,#1e3a8a,#1d4ed8)", boxShadow: "0 2px 10px rgba(29,78,216,0.35)", border: "none" }}>
                                    <Save size={15} />
                                    {formLoading ? "Saving..." : "Save Record"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ══════════════ VIEW MODAL ══════════════ */}
            {selectedRow && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={e => { if (e.target === e.currentTarget) setSelectedRow(null); }}>
                    <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden" style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.40)" }}>

                        <div style={gradHdr} className="px-7 py-5 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="bg-white/15 border border-white/20 p-2.5 rounded-2xl"><ClipboardList size={20} className="text-white" /></div>
                                <div>
                                    <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em] mb-0.5">Mega Soft · Spareparts Department</p>
                                    <h2 className="text-white font-extrabold text-base leading-none">Motorpool Area Compacted Scrap</h2>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="text-right bg-white/10 border border-white/20 rounded-xl px-4 py-2">
                                    <p className="text-blue-300 text-[9px] font-bold uppercase tracking-widest">Control No.</p>
                                    <p className="text-white font-extrabold text-sm tracking-wider">{f.controlNo(selectedRow)}</p>
                                </div>
                                <button onClick={() => setSelectedRow(null)} className="bg-white/15 hover:bg-white/25 text-white rounded-xl w-9 h-9 flex items-center justify-center transition-all cursor-pointer" style={{ border: "none" }}>
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Meta bar */}
                        <div className="px-7 py-2.5 flex items-center gap-1 flex-wrap flex-shrink-0" style={{ background: "rgba(15,23,42,0.92)" }}>
                            {[
                                { icon: CalendarDays, label: "Date", value: f.date(selectedRow) ? new Date(f.date(selectedRow)).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—" },
                                { icon: Users, label: "Compactor", value: f.compactor(selectedRow) || "—" },
                                { icon: Building2, label: "Recorded By", value: f.recordedBy(selectedRow) || "—" },
                            ].map((item, i) => {
                                const Icon = item.icon;
                                return (
                                    <React.Fragment key={i}>
                                        {i > 0 && <span className="w-px h-4 bg-white/20 mx-3" />}
                                        <span className="flex items-center gap-2 text-xs">
                                            <Icon size={12} className="text-blue-400" />
                                            <span className="text-white/50">{item.label}:</span>
                                            <span className="text-white font-semibold">{item.value}</span>
                                        </span>
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        {/* Document preview */}
                        <div className="flex-1 overflow-y-auto" style={{ background: "#e8edf5" }}>
                            <div className="p-6">
                                <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
                                    <MotorpoolDocument data={selectedRow} scrapCodes={scrapCodes} />
                                </div>
                            </div>
                        </div>

                        {/* Footer — Export PDF only */}
                        <div className="px-7 py-4 bg-white border-t border-gray-100 flex items-center justify-between flex-shrink-0" style={{ boxShadow: "0 -4px 16px rgba(0,0,0,0.06)" }}>
                            <button onClick={() => handleExportPDF(selectedRow)} disabled={pdfLoading}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-all cursor-pointer hover:-translate-y-0.5"
                                style={{ background: "linear-gradient(135deg,#dc2626,#b91c1c)", boxShadow: "0 2px 10px rgba(220,38,38,0.35)", border: "none" }}>
                                <Download size={15} />
                                {pdfLoading ? "Generating PDF..." : "Export PDF"}
                            </button>
                            <button onClick={() => setSelectedRow(null)}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 text-sm font-bold transition-all cursor-pointer">
                                <X size={14} /> Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MotorpoolScrap;